import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import socketService from '../services/socket';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Check portal type for storage keys
  const portalType = process.env.REACT_APP_PORTAL_TYPE || 'admin';

  // Use portal-specific localStorage keys to prevent cross-portal interference
  const STORAGE_KEYS = {
    token: `${portalType}_token`,
    user: `${portalType}_user`
  };

  const [user, setUser] = useState(() => {
    // First, check if there's an existing session
    const savedUser = localStorage.getItem(STORAGE_KEYS.user);
    const savedToken = localStorage.getItem(STORAGE_KEYS.token);

    if (savedUser && savedToken) {
      try {
        const parsedUser = JSON.parse(savedUser);
        // If there's a real user session, use it (don't overwrite with mock data)
        return parsedUser;
      } catch (error) {
        localStorage.removeItem(STORAGE_KEYS.user);
        localStorage.removeItem(STORAGE_KEYS.token);
      }
    }



    return null;
  });

  const [loading, setLoading] = useState(false);

  // Initialize socket connection when user is authenticated
  useEffect(() => {
    if (user) {
      const token = localStorage.getItem(STORAGE_KEYS.token);
      if (token) {
        socketService.connect(token);
      }
    } else {
      socketService.disconnect();
    }

    return () => {
      // Cleanup on unmount
      if (!user) {
        socketService.disconnect();
      }
    };
  }, [user, STORAGE_KEYS.token]);

  const login = async (credentials) => {
    try {
      setLoading(true);


      // Production login
      const response = await authAPI.login(credentials);
      const { token, user } = response.data;

      // Use portal-specific storage keys
      localStorage.setItem(STORAGE_KEYS.token, token);
      localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
      localStorage.setItem('userId', user._id); // Store userId separately for socket
      setUser(user);

      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {


    // Clear portal-specific storage
    localStorage.removeItem(STORAGE_KEYS.token);
    localStorage.removeItem(STORAGE_KEYS.user);
    localStorage.removeItem('userId'); // Clear userId
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(userData));
  };

  // Add setUser for OTP login
  const setUserData = (userData) => {
    setUser(userData);

    if (userData?.token) {
      // ✅ Always prefer the real JWT from backend
      localStorage.setItem(STORAGE_KEYS.token, userData.token);
    } else if (process.env.NODE_ENV !== 'production') {
      // ⚡ Only in dev, allow bypass token
      localStorage.setItem(STORAGE_KEYS.token, 'dev-admin-token');
    } else {
      console.error('❌ No JWT token returned from backend login!');
    }

    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(userData));
    if (userData?._id) {
      localStorage.setItem('userId', userData._id); // Store userId separately for socket
    }
  };


  const value = {
    user,
    login,
    logout,
    updateUser,
    setUser: setUserData,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.userType === 'admin',
    isTeacher: user?.userType === 'teacher',
    isStudent: user?.userType === 'student',
    STORAGE_KEYS
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};